import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { KyResponse } from "ky";
import type {
  CreateDepartmentsLambdaResponse,
  CreateDepartmentsVariables,
  DeleteDepartmentVariables,
  Department,
  GetDepartmentsParams,
  UpdateDepartmentVariables
} from "@dealer-center/ddc-pages-departments-interface";
import {
  createDepartments,
  deleteDepartment,
  getDepartments,
  updateDepartment
} from "./functions";
import { useToaster } from "@dealer-center/core-components";

const DEPARTMENTS_QUERY_KEY = "ddc-pages-departments" as const;

// ============================================================================
// Hooks
// ============================================================================

export function useGetDepartments({ accountId }: GetDepartmentsParams) {
  return useQuery<Department[], Error>({
    queryKey: [DEPARTMENTS_QUERY_KEY, { accountId }],
    queryFn: () => getDepartments(accountId?.trim() || ""),
    enabled: !!accountId && accountId.trim().length > 0,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}
export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();

  return useMutation<KyResponse, Error, DeleteDepartmentVariables>({
    // CRITICAL: Disable retries to prevent duplicate delete operations.
    // Lambda implements Saga Pattern with explicit rollback logic.
    // Auto-retries could cause inconsistent state if department is deleted
    // but hours rollback is attempted on retry.
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] });
      toastSuccess("Success", "Department deleted");
    },
    onError: () => {
      toastError("Error deleting department");
    },
    mutationFn: variables => {
      return deleteDepartment(variables.accountId, variables.department);
    }
  });
}

export function useCreateDepartments() {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();

  return useMutation<
    CreateDepartmentsLambdaResponse,
    Error,
    CreateDepartmentsVariables
  >({
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] });

      // Show different messages based on success/failure counts
      if (data.failureCount === 0) {
        toastSuccess(
          "Success",
          `All ${data.successCount} department(s) created successfully`
        );
      } else if (data.successCount === 0) {
        toastError(`Failed to create all ${data.failureCount} department(s)`);
      } else {
        // Partial success (207 Multi-Status)
        toastSuccess(
          "Partial Success",
          `${data.successCount} department(s) created, ${data.failureCount} failed`
        );
      }
    },
    onError: error => {
      toastError(`Error creating departments: ${error.message}`);
    },
    mutationFn: variables => {
      return createDepartments(
        variables.accountId,
        variables.departments,
        variables.userId,
        variables.actingAsUserId
      );
    }
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();

  return useMutation<KyResponse, Error, UpdateDepartmentVariables>({
    // CRITICAL: Disable retries to prevent duplicate operations.
    // The lambda performs multi-step updates (create new hours, update dept, delete old hours).
    // Auto-retries could create duplicate hours records if the operation partially succeeds
    // but the response is lost due to network issues.
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] });
      toastSuccess("Success", "Department updated");
    },
    onError: () => {
      toastError("Error updating department");
    },
    mutationFn: variables => {
      return updateDepartment(
        variables.accountId,
        variables.oldType,
        variables.newType,
        variables.newLabel,
        variables.userId,
        variables.actingAsUserId
      );
    }
  });
}
